import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Empty,
  message,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import {
  ArrowLeftOutlined,
  BankOutlined,
  DollarOutlined,
  ExportOutlined,
  ReloadOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { fetchWorkspace } from "services/workspaceService";
import paymentChargesService from "services/paymentChargesService";
import paymentMethodsService from "services/paymentMethodsService";
import { readable } from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 10;
const DEFAULT_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const TIME_ZONE_OPTIONS = Array.from(
  new Set([
    DEFAULT_TIME_ZONE,
    "UTC",
    "America/Los_Angeles",
    "America/Chicago",
    "America/New_York",
    "Africa/Lagos",
  ]),
).map((zone) => ({ value: zone, label: zone }));

const EARNING_STATUS_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "PAID_OUT", label: "Paid out" },
  { value: "SUCCESSFUL", label: "Successful" },
  { value: "DISPUTED", label: "Disputed" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "REFUNDED", label: "Refunded" },
];

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const pageTotal = (data) =>
  typeof data?.totalElements === "number" ? data.totalElements : pageContent(data).length;

const boolValue = (value) => value === true || String(value).toLowerCase() === "true";

const lastSecondOfDay = (value) => value.hour(23).minute(59).second(59).millisecond(999);

const defaultDateRange = () => [
  dayjs().subtract(7, "day").startOf("day"),
  lastSecondOfDay(dayjs()),
];

const dateRangeToParams = (range) => {
  const [start, end] = range || [];
  return {
    start: start ? start.startOf("day").toISOString() : undefined,
    end: end ? lastSecondOfDay(end).toISOString() : undefined,
  };
};

const dateRangeLabel = (range) => {
  const [start, end] = range || [];
  if (!start || !end) return "All available dates";
  return `${start.format("MMM D, YYYY HH:mm:ss")} - ${end.format("MMM D, YYYY HH:mm:ss")}`;
};

const fullLocalTimestamp = (value, timeZone = DEFAULT_TIME_ZONE) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
    timeZone,
  }).format(date);
};

const money = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
  }).format(number);
};

const nameOf = (user, fallback = "-") => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.email || user?.id || fallback;
};

const activeAccountUserId = (account, fallbackUserId) => {
  const users = account?.users || [];
  const active = users.find((member) => {
    const status = String(member?.membershipStatus || "").toUpperCase();
    return status === "ACTIVE" || status === "ACCEPTED" || status === "";
  });
  return active?.user?.id || fallbackUserId || "";
};

const isHelperWorkspace = (workspace) => {
  const use = String(
    workspace?.workspaceUse ||
      workspace?.workspaceUseName ||
      workspace?.use ||
      workspace?.useType ||
      "",
  ).toUpperCase();
  return use === "HELPER" || use === "HIRING";
};

const incomingAmountOf = (charge) => Number(charge?.receivableAmount ?? charge?.amount ?? 0);

const payoutDisplayAmountOf = (payout) => Number(payout?.netPayout ?? payout?.amount ?? 0);

const payoutAccountLabel = (account) => {
  if (!account) return "No payout method set";
  const brand =
    account.bankName ||
    account.bank ||
    account.institutionName ||
    account.brand ||
    account.accountHolderName ||
    "External Account";
  const type = account.objectType || account.type || account.accountType;
  const last4 = account.last4 || account.lastDigits || account.last4Digits;
  return [brand, type ? `(${type})` : "", last4 ? `••••${last4}` : ""]
    .filter(Boolean)
    .join(" ");
};

const chargeAmount = (grossAmount, charge) => {
  if (!grossAmount || grossAmount <= 0 || !charge?.value || charge.value <= 0) return 0;
  const value = Number(charge.value);
  return String(charge.valueType || "").toUpperCase() === "PERCENTAGE"
    ? grossAmount * value
    : value;
};

const buildChargeBreakdown = (grossAmount, charges = []) =>
  [...charges]
    .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0))
    .map((charge) => {
      const valueType = String(charge.valueType || "").toUpperCase();
      const rateValue = Number(charge.value || 0);
      return {
        chargeId: charge.id,
        name: charge.name || charge.code || "Payout Charge",
        code: charge.code,
        valueType,
        rateValue,
        percentageText: valueType === "PERCENTAGE" ? `${(rateValue * 100).toFixed(2)}%` : "",
        amount: chargeAmount(grossAmount, charge),
      };
    });

const buildCashOutChargesPayload = (charges = [], grossAmount) =>
  [...charges]
    .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0))
    .map((charge) => ({
      chargeId: charge.id,
      code: charge.code,
      name: charge.name || charge.code || "Payout Charge",
      category: charge.category,
      valueType: charge.valueType,
      value: charge.value,
      applyOn: charge.applyOn,
      displayOrder: charge.displayOrder,
      chargeTarget: charge.chargeTarget,
      currency: charge.currency,
      country: charge.country,
      state: charge.state,
      county: charge.county,
      city: charge.city,
      minAmount: charge.minAmount,
      maxAmount: charge.maxAmount,
      amount: chargeAmount(grossAmount, charge),
    }));

const WorkspaceEarningsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [account, setAccount] = useState(null);
  const [activeTab, setActiveTab] = useState("incoming");
  const [earnings, setEarnings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [availableCharges, setAvailableCharges] = useState([]);
  const [defaultPayoutAccount, setDefaultPayoutAccount] = useState(null);
  const [connectAccount, setConnectAccount] = useState(null);
  const [effectiveCharges, setEffectiveCharges] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [cashOutSubmitting, setCashOutSubmitting] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [earningStatus, setEarningStatus] = useState("ALL");
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [selectedTimeZone, setSelectedTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [cashOutOpen, setCashOutOpen] = useState(false);
  const dateRangeRef = useRef(dateRange);

  const payeeId = useMemo(() => activeAccountUserId(account, actorId), [account, actorId]);

  const loadEarnings = useCallback(async (nextPage = 1, rangeOverride = dateRangeRef.current, statusOverride = earningStatus) => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const rangeParams = dateRangeToParams(rangeOverride);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount);

      if (!isHelperWorkspace(nextAccount)) {
        setEarnings([]);
        setTotal(0);
        setPage(nextPage);
        return;
      }

      const result = await paymentChargesService.receivable.listForOrganization({
        actorId,
        userId: activeAccountUserId(nextAccount, actorId),
        workspaceId,
        status: statusOverride === "ALL" ? undefined : statusOverride,
        start: rangeParams.start,
        end: rangeParams.end,
        page: nextPage - 1,
        size: PAGE_SIZE,
      });

      const scoped = pageContent(result).filter((item) => {
        const statusMatches =
          statusOverride === "ALL" ||
          String(item.paymentStatus || item.status || "").toUpperCase() === statusOverride;
        const itemWorkspaceId =
          item.payeeWorkspaceId ||
          item.payeeWorkspace?.id ||
          item.paymentChargeDetails?.payeeWorkspaceId ||
          item.paymentChargeDetails?.payeeWorkspace?.id;
        const workspaceMatches = itemWorkspaceId
          ? String(itemWorkspaceId) === String(workspaceId)
          : true;
        return statusMatches && workspaceMatches;
      });

      setEarnings(scoped);
      setTotal(pageTotal(result));
      setPage(nextPage);
    } catch (error) {
      message.error(error?.message || "Failed to load earnings.");
    } finally {
      setLoading(false);
    }
  }, [actorId, earningStatus, workspaceId]);

  const loadPayouts = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setPayoutLoading(true);
      const nextAccount = account || await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount);

      if (!isHelperWorkspace(nextAccount)) {
        setPayouts([]);
        setAvailableCharges([]);
        setDefaultPayoutAccount(null);
        setConnectAccount(null);
        setEffectiveCharges([]);
        return;
      }

      const nextPayeeId = activeAccountUserId(nextAccount, actorId);
      const [nextPayouts, nextAvailableCharges, nextDefaultAccount, nextConnectAccount] = await Promise.all([
        paymentChargesService.payouts.listReceived({
          actorId,
          userId: nextPayeeId,
          payeeWorkspaceId: workspaceId,
        }),
        paymentChargesService.earnings.listAvailableCharges({
          actorId,
          payeeId: nextPayeeId,
          payeeWorkspaceId: workspaceId,
          status: "SUCCESSFUL",
        }),
        paymentChargesService.payouts.getDefaultExternalAccount({
          actorId,
          workspaceId,
        }).catch(() => null),
        paymentMethodsService.incoming.getConnectAccount({
          actorId,
          userId: nextPayeeId,
          workspaceId,
          refresh: false,
        }).catch(() => null),
      ]);

      const successfulAvailable = nextAvailableCharges.filter((charge) =>
        String(charge.paymentStatus || charge.status || "").toUpperCase() === "SUCCESSFUL",
      );
      const availableAmount = successfulAvailable.reduce(
        (sum, charge) => sum + Number(charge.receivableAmount || 0),
        0,
      );
      const nextEffectiveCharges = await paymentChargesService.payouts.listEffectiveCharges({
        actorId,
        amount: availableAmount,
        currency: successfulAvailable[0]?.currency || "USD",
      });

      setPayouts(nextPayouts);
      setAvailableCharges(successfulAvailable);
      setDefaultPayoutAccount(nextDefaultAccount);
      setConnectAccount(nextConnectAccount);
      setEffectiveCharges(nextEffectiveCharges);
    } catch (error) {
      message.error(error?.message || "Failed to load payout details.");
      setPayouts([]);
      setAvailableCharges([]);
      setDefaultPayoutAccount(null);
      setConnectAccount(null);
      setEffectiveCharges([]);
    } finally {
      setPayoutLoading(false);
    }
  }, [account, actorId, workspaceId]);

  useEffect(() => {
    loadEarnings(1);
  }, [loadEarnings]);

  useEffect(() => {
    if (activeTab === "payouts") {
      loadPayouts();
    }
  }, [activeTab, loadPayouts]);

  const incomingTotal = useMemo(
    () => earnings.reduce((sum, charge) => sum + incomingAmountOf(charge), 0),
    [earnings],
  );

  const availableEarnings = useMemo(
    () => availableCharges.reduce((sum, charge) => sum + Number(charge.receivableAmount || 0), 0),
    [availableCharges],
  );

  const payoutBreakdown = useMemo(
    () => buildChargeBreakdown(availableEarnings, effectiveCharges),
    [availableEarnings, effectiveCharges],
  );

  const payoutChargesTotal = useMemo(
    () => payoutBreakdown.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [payoutBreakdown],
  );

  const isStripeOnboarded = boolValue(connectAccount?.onboarded);
  const netCashOutAmount = Math.max(0, availableEarnings - payoutChargesTotal);

  const updateDateRange = (nextRange) => {
    const normalizedRange = nextRange || defaultDateRange();
    dateRangeRef.current = normalizedRange;
    setDateRange(normalizedRange);
    loadEarnings(1, normalizedRange);
  };

  const updateStatus = (nextStatus) => {
    setEarningStatus(nextStatus);
    loadEarnings(1, dateRangeRef.current, nextStatus);
  };

  const openStripeDashboard = async () => {
    try {
      setDashboardLoading(true);
      const result = await paymentChargesService.payouts.createDashboardLink({
        actorId,
        userId: payeeId || actorId,
        workspaceId,
      });
      const url = result?.url || result;
      if (!url) {
        message.warning("Stripe Dashboard link is not available right now.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      message.error(error?.message || "Unable to open Stripe Dashboard.");
    } finally {
      setDashboardLoading(false);
    }
  };

  const confirmCashOut = async () => {
    if (!availableCharges.length || availableEarnings <= 0) {
      message.warning("You have no available earnings to cash out.");
      return;
    }
    if (!defaultPayoutAccount) {
      message.warning("Please set a default payout method in Wallet first.");
      return;
    }
    if (netCashOutAmount <= 0) {
      message.warning("Payout deductions are equal to or greater than your available earnings.");
      return;
    }

    try {
      setCashOutSubmitting(true);
      await paymentChargesService.earnings.cashOut({
        actorId,
        chargeIds: availableCharges.map((charge) => charge.id),
        payeeId,
        payeeWorkspaceId: workspaceId,
        currency: availableCharges[0]?.currency || "USD",
        totalAmount: availableEarnings,
        totalCharges: payoutChargesTotal,
        netPayout: netCashOutAmount,
        charges: buildCashOutChargesPayload(effectiveCharges, availableEarnings),
      });
      message.success(`Cash out submitted for ${money(netCashOutAmount, availableCharges[0]?.currency || "USD")}.`);
      setCashOutOpen(false);
      await Promise.all([loadPayouts(), loadEarnings(1)]);
    } catch (error) {
      message.error(error?.message || "Unable to process payout.");
    } finally {
      setCashOutSubmitting(false);
    }
  };

  const earningColumns = [
    {
      title: "Earning",
      key: "earning",
      width: 390,
      render: (_, charge) => (
        <div style={{ minWidth: 0 }}>
          <Text strong style={{ display: "block", wordBreak: "break-word" }}>
            {charge.description || readable(charge.chargeItemCode, "Earning")}
          </Text>
          <Space wrap size={[4, 4]} style={{ marginTop: 4 }}>
            {charge.chargeItemCode ? <Tag color="blue">{readable(charge.chargeItemCode)}</Tag> : null}
            {charge.clientApp ? <Tag color="cyan">{readable(charge.clientApp)}</Tag> : null}
          </Space>
        </div>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 150,
      render: (_, charge) => (
        <div style={{ whiteSpace: "nowrap" }}>
          <Text strong>{money(incomingAmountOf(charge), charge.currency)}</Text>
          {charge.chargeableAmount !== undefined ? (
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              Gross {money(charge.chargeableAmount, charge.currency)}
            </Text>
          ) : null}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 145,
      render: (_, charge) => (
        <Tag color="green" style={{ marginInlineEnd: 0, whiteSpace: "normal" }}>
          {readable(charge.paymentStatus || charge.status, "Status")}
        </Tag>
      ),
    },
    {
      title: "Payer",
      key: "payer",
      width: 220,
      render: (_, charge) => (
        <div style={{ minWidth: 0 }}>
          <Text ellipsis style={{ display: "block" }}>{nameOf(charge.payer, "Unknown")}</Text>
          <Text ellipsis type="secondary" style={{ display: "block", fontSize: 12 }}>
            {charge.payerWorkspace?.name || charge.payerWorkspaceId || "-"}
          </Text>
        </div>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 230,
      render: (value) => <Text style={{ whiteSpace: "nowrap" }}>{fullLocalTimestamp(value, selectedTimeZone)}</Text>,
    },
  ];

  const availableColumns = [
    ...earningColumns.slice(0, 3),
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 230,
      render: (value) => <Text style={{ whiteSpace: "nowrap" }}>{fullLocalTimestamp(value, selectedTimeZone)}</Text>,
    },
  ];

  const payoutColumns = [
    {
      title: "Payout",
      key: "payout",
      width: 320,
      render: (_, payout) => (
        <Space direction="vertical" size={2}>
          <Text strong>{payout.stripeTransferId || payout.id}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {payout.recipientWorkspace?.name || payout.recipientWorkspaceId || workspaceId}
          </Text>
        </Space>
      ),
    },
    {
      title: "Net Payout",
      key: "netPayout",
      width: 150,
      render: (_, payout) => (
        <Space direction="vertical" size={2}>
          <Text strong>{money(payoutDisplayAmountOf(payout), payout.currency)}</Text>
          {payout.totalCharges != null ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Fees {money(payout.totalCharges, payout.currency)}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (value) => <Tag color="blue" style={{ whiteSpace: "normal" }}>{readable(value, "Status")}</Tag>,
    },
    {
      title: "Transferred",
      key: "transferredAt",
      width: 230,
      render: (_, payout) => (
        <Text style={{ whiteSpace: "nowrap" }}>
          {fullLocalTimestamp(payout.transferredAt || payout.createdAt, selectedTimeZone)}
        </Text>
      ),
    },
  ];

  const renderPayoutSummary = () => (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space wrap>
        <Button icon={<WalletOutlined />} onClick={() => navigate(paths.workspacePaymentMethods(workspaceId))}>
          Wallet
        </Button>
        {isStripeOnboarded ? (
          <Button
            type="link"
            icon={<ExportOutlined />}
            loading={dashboardLoading}
            onClick={openStripeDashboard}
            style={{ color: "#1677ff", paddingInline: 0 }}
          >
            Stripe Dashboard
          </Button>
        ) : (
          <Button
            type="link"
            icon={<WalletOutlined />}
            onClick={() => navigate(paths.workspacePaymentMethods(workspaceId))}
            style={{ color: OLIVEEB_COLORS.oliveebGreen, paddingInline: 0 }}
          >
            Manage Payment Methods
          </Button>
        )}
      </Space>

      <Card size="small" style={workspaceStyles.softCard}>
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 2, md: 4 }}
          items={[
            { key: "available", label: "Available Earnings", children: money(availableEarnings, availableCharges[0]?.currency || "USD") },
            { key: "charges", label: "Payout Charges", children: money(payoutChargesTotal, availableCharges[0]?.currency || "USD") },
            { key: "net", label: "Net Payout", children: <Text strong>{money(netCashOutAmount, availableCharges[0]?.currency || "USD")}</Text> },
            { key: "method", label: "Payout To", children: payoutAccountLabel(defaultPayoutAccount) },
          ]}
        />
        {payoutBreakdown.length ? (
          <Space direction="vertical" size={6} style={{ marginTop: 12, width: "100%" }}>
            {payoutBreakdown.map((item) => (
              <div key={item.chargeId} className="d-flex justify-content-between gap-3">
                <Text type="secondary">
                  {item.name}{item.percentageText ? ` (${item.percentageText})` : ""}
                </Text>
                <Text type="danger">-{money(item.amount, availableCharges[0]?.currency || "USD")}</Text>
              </div>
            ))}
          </Space>
        ) : null}
        <Button
          type="primary"
          icon={<BankOutlined />}
          disabled={!availableCharges.length || !defaultPayoutAccount || netCashOutAmount <= 0}
          loading={cashOutSubmitting}
          onClick={() => setCashOutOpen(true)}
          style={{ marginTop: 14, background: OLIVEEB_COLORS.oliveebGreen }}
        >
          Cash Out
        </Button>
      </Card>
    </Space>
  );

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="dollar-sign"
          title={activeTab === "incoming" ? "Earnings: Payments Received" : "Earnings: Payouts"}
          subtitle="Review receivable payments and payouts for this helper workspace"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.workspaceDetails(workspaceId))}>
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={() => activeTab === "incoming" ? loadEarnings(page) : loadPayouts()}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!isHelperWorkspace(account) ? (
            <Alert type="warning" showIcon message="Earnings are available for helper workspaces only." />
          ) : null}

          {isHelperWorkspace(account) ? (
            <>
          <Card size="small" style={workspaceStyles.softCard}>
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <Space size={8} wrap>
                <DollarOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>Payee workspace</Text>
                <Tag color="green">{account?.name || workspaceId}</Tag>
                <Tag color="blue">{dateRangeLabel(dateRange)}</Tag>
                <Tag color="cyan">{selectedTimeZone}</Tag>
              </Space>
              <Space size={8} wrap>
                <Text type="secondary">{activeTab === "incoming" ? `${earnings.length} payments` : `${availableCharges.length} available payments`}</Text>
                <Text strong>{money(activeTab === "incoming" ? incomingTotal : availableEarnings, "USD")}</Text>
              </Space>
            </div>
          </Card>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: "incoming", label: "Earnings" },
              { key: "payouts", label: "Payouts" },
            ]}
          />

          {activeTab === "incoming" ? (
            <>
              <Card size="small" style={{ ...workspaceStyles.softCard, background: OLIVEEB_COLORS.card }}>
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                  <Space wrap>
                    <RangePicker
                      value={dateRange}
                      allowClear={false}
                      showTime={{ defaultValue: [dayjs().startOf("day"), lastSecondOfDay(dayjs())] }}
                      format="MMM D, YYYY HH:mm:ss"
                      onChange={updateDateRange}
                      disabledDate={(current) => current && current.isAfter(dayjs().endOf("day"))}
                    />
                    <Select
                      value={earningStatus}
                      options={EARNING_STATUS_FILTERS}
                      style={{ width: 170 }}
                      onChange={updateStatus}
                    />
                    <Select
                      showSearch
                      value={selectedTimeZone}
                      options={TIME_ZONE_OPTIONS}
                      style={{ width: 240 }}
                      onChange={setSelectedTimeZone}
                      optionFilterProp="label"
                    />
                  </Space>
                </div>
              </Card>

              <Card styles={{ body: { padding: 0 } }} style={{ borderRadius: 8, overflow: "hidden", borderColor: OLIVEEB_COLORS.border }}>
                <Table
                  rowKey={(record) => record.id || record.chargeItemId || record.createdAt}
                  loading={loading}
                  columns={earningColumns}
                  dataSource={earnings}
                  size="small"
                  tableLayout="fixed"
                  pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, onChange: (nextPage) => loadEarnings(nextPage) }}
                  scroll={{ x: 1135 }}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payments received yet" style={{ padding: "40px 0" }} /> }}
                />
              </Card>
            </>
          ) : (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              {payoutLoading && !availableCharges.length && !payouts.length ? (
                <Card><Skeleton active paragraph={{ rows: 6 }} /></Card>
              ) : renderPayoutSummary()}

              <Card title="Available Payments" styles={{ body: { padding: 0 } }} style={{ borderRadius: 8, overflow: "hidden", borderColor: OLIVEEB_COLORS.border }}>
                <Table
                  rowKey={(record) => record.id || record.chargeItemId || record.createdAt}
                  loading={payoutLoading}
                  columns={availableColumns}
                  dataSource={availableCharges}
                  size="small"
                  tableLayout="fixed"
                  pagination={false}
                  scroll={{ x: 915 }}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No available payments" style={{ padding: "32px 0" }} /> }}
                />
              </Card>

              <Card title="Payout Activity" styles={{ body: { padding: 0 } }} style={{ borderRadius: 8, overflow: "hidden", borderColor: OLIVEEB_COLORS.border }}>
                <Table
                  rowKey={(record) => record.id || record.stripeTransferId || record.createdAt}
                  loading={payoutLoading}
                  columns={payoutColumns}
                  dataSource={payouts}
                  size="small"
                  tableLayout="fixed"
                  pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
                  scroll={{ x: 840 }}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payouts yet" style={{ padding: "32px 0" }} /> }}
                />
              </Card>
            </Space>
          )}
            </>
          ) : null}
        </Space>
      )}

      <Modal
        title="Cash Out Confirmation"
        open={cashOutOpen}
        confirmLoading={cashOutSubmitting}
        okText="Confirm Cash Out"
        onOk={confirmCashOut}
        onCancel={() => !cashOutSubmitting && setCashOutOpen(false)}
      >
        <Descriptions
          size="small"
          column={1}
          items={[
            { key: "total", label: "Total Amount", children: money(availableEarnings, availableCharges[0]?.currency || "USD") },
            { key: "charges", label: "Total Charges", children: <Text type="danger">-{money(payoutChargesTotal, availableCharges[0]?.currency || "USD")}</Text> },
            { key: "net", label: "Net Payout", children: <Text strong>{money(netCashOutAmount, availableCharges[0]?.currency || "USD")}</Text> },
            { key: "method", label: "Payout To", children: payoutAccountLabel(defaultPayoutAccount) },
          ]}
        />
      </Modal>
    </div>
  );
};

export default WorkspaceEarningsPage;
